import { createApi } from '@reduxjs/toolkit/query/react';
import { storage } from '@doxyme/ui/utils';
import { type Room, transformRooms } from '../../waiting-room';
import type {
  Customer,
  Source,
  GetAnalyticsData,
  GetAnalyticsOverviewData,
  GetAnalyticsOverviewPayload,
  GetAnalyticsPayload,
  Purchase,
  WaitingRoomSettingsFields,
  CreateWaitingRoomPayload,
  PaymentIntent,
} from '../users.types';
import { ogBaseQuery } from '../../ogBaseQuery';
import { getProviderName } from '../../../shared/getProviderName';
import { StorageKeys } from '../../../shared/constants';

export type LbUserIdProps = {
  id: number;
  hash: string;
};

export type LbUserNameProps = {
  title?: string | null;
  firstName: string;
  lastName: string;
  displayName?: string | null;
  email: string;
  confirmNewEmail: string;
};

export enum LbUserPictureProvider {
  GRAVATAR = 'gravatar',
  DOXYME = 'doxymeProfilePicture',
  LINKEDIN = 'LinkedIn',
}

export type LbUserPictureProps = {
  useProfilePicture?: boolean;
  profilePictureProvider?: LbUserPictureProvider | null;
  userProfilePictureUrl?: string | null;
};

export type LbUserLangProps = {
  preferredLocale: string | null;
};

export type LbUserInvitationProps = {
  preferredInviteCountry: string | null;
  smsVerified: boolean | null;
};

export type LbUserIdentity = {
  id: string;
  userId: number;
  providerId: string;
  provider: string;
  pictureUrl: string;
  created: Date;
};

export type LbUserSubscriptionData = {
  currency: string;
  dayPassEndDate?: string | null;
};

export type LbUserVideoQualitySetting = {
  videoQuality?: string | null;
};

export type LbUserIdentities = {
  identities: LbUserIdentity[];
};

export type LbSoundMessage =
  | 'done-deal'
  | 'hammered'
  | 'likeable'
  | 'waitingfor'
  | 'wakeup-call';

export type LbCallStartSound = 'doorbell' | 'chime' | 'ta-da' | 'waitingfor';

export type LbUserNotificationProps = {
  /**
   * Which sound to play when a chat message is received.
   */
  soundMessage: LbSoundMessage | '';

  /**
   * Should a sound play when a patient checks in?
   */
  browserNotifications: boolean;

  /**
   * Which sound to play when a patient checks in.
   */
  soundNotification: LbSoundMessage | '';

  /**
   * Which sound to play when the call starts
   */
  callStartSound: LbCallStartSound | null;

  emailNotifications: boolean;
  emailNotificationsAddress: string;

  smsNotifications: boolean;
  smsNotificationsPhone: string;
};

export type LbUserBaa = {
  noBaa: boolean;
};

export type LbUserPassword = {
  currentPassword: string;
  password: string;
  confirmPassword: string;
};

export type LbUserCustomer = {
  customerId?: string;
};

type UserType = 'user' | 'admin';

export type LbUserType = {
  type: UserType;
};

export type LbUserProfessionalInfo = {
  NPI: number | string | null;
  speciality?: string | null;
  designation?: string | null;
};

export type LbUserRecordInfo = {
  created: string;
};

export type LbUserPlan = {
  trialEndDate: string;
  betaAccesses: boolean;
};

export type LbUserPrototypeAppsAccess = {
  hasOptedInToPrototypeApps?: boolean;
};

export type LbUser = LbUserIdProps &
  LbUserNameProps &
  LbUserPictureProps &
  LbUserLangProps &
  LbUserInvitationProps &
  LbUserNotificationProps &
  LbUserBaa &
  LbUserCustomer &
  LbUserType &
  LbUserPassword &
  LbUserProfessionalInfo &
  LbUserRecordInfo &
  LbUserPlan &
  LbUserPrototypeAppsAccess &
  LbUserIdentities &
  LbUserSubscriptionData &
  LbUserVideoQualitySetting;

type LbCurrentTime = {
  ts: number;
};

type LbLocationDetails = {
  clientIp: string;
  location: {
    country_code: string;

    country_name: string;

    region_name: string;
  };
};

export type ProviderUser = LbUser & {
  username: string;
};

export type LbStateLicensed = {
  country: string;
  state: string;
  id: number;
  userId: number;
  institutionId: number | null;
};

// TO-DO - add shouldRedirect to this type and return from OG
export type Workspace = {
  id: string;
  institutionId?: string;
  type: 'free' | 'professional' | 'clinic';
  name: string;
  role: 'admin' | 'owner' | 'member';
  status: 'active' | 'inactive' | 'closed';
  url: string;
  active?: boolean;
  shouldRedirect?: boolean;
  useSso?: boolean;
};

export type LbWorkspaceResponse = {
  defaultWorkspace: string | null;
  workspaces: Workspace[];
};

const transformGetUserResponse = (response: LbUser): ProviderUser => ({
  ...response,
  username: getProviderName(response),
});

type LinkedInDisplayImageElementIdentifier = {
  identifier?: string;
};
type LinkedInDisplayImageElement = {
  identifiers?: LinkedInDisplayImageElementIdentifier[];
};
type LinkedInDisplayImage = {
  elements?: LinkedInDisplayImageElement[];
};
export type LinkedInProfilePicture = {
  'displayImage~'?: LinkedInDisplayImage;
};
export type LinkedInData = {
  profilePicture?: LinkedInProfilePicture;
};

export type ValidateCaptchaRequestBody = {
  /**
   * ReCaptcha token generated by google recaptcha client.
   */
  gRecaptchaResponseToken: string;
};

export type ValidateCaptchaResponseData = {
  /**
   * JWT token
   */
  token: string;
};

export const usersOGService = createApi({
  baseQuery: ogBaseQuery(),
  reducerPath: 'providerUserState',
  tagTypes: ['users', 'rooms', 'analytics', 'analytics-overview'],
  endpoints: (builder) => ({
    getUser: builder.query<ProviderUser, { userId: string }>({
      query: (data) => {
        return {
          url: `users/${data.userId}`,
          method: 'GET',
          params: {
            filter: JSON.stringify({
              include: ['identities'],
            }),
          },
        };
      },
      providesTags: ['users'],
      transformResponse: transformGetUserResponse,
    }),
    updateUser: builder.mutation<
      LbUser,
      {
        userId: string;
        payload: Partial<LbUser>;
      }
    >({
      query: (data) => {
        return {
          url: `users/${data.userId}`,
          method: 'PUT',
          body: data.payload,
        };
      },
      async onQueryStarted({ userId }, { dispatch, queryFulfilled }) {
        const { data: updatedUser } = await queryFulfilled;
        dispatch(
          usersOGService.util.updateQueryData(
            'getUser',
            { userId },
            (draft) => {
              Object.assign(draft, transformGetUserResponse(updatedUser));
            },
          ),
        );
      },
    }),
    setPrototypeAppsOptIn: builder.mutation<
      LbUser,
      {
        userId: string;
      }
    >({
      query: ({ userId }) => {
        return {
          url: `users/${userId}`,
          method: 'PUT',
          body: { hasOptedInToPrototypeApps: true },
        };
      },
      async onQueryStarted({ userId }, { dispatch, queryFulfilled }) {
        const { data: updatedUser } = await queryFulfilled;
        dispatch(
          usersOGService.util.updateQueryData(
            'getUser',
            { userId },
            (draft) => {
              Object.assign(draft, transformGetUserResponse(updatedUser));
            },
          ),
        );
      },
    }),
    updateRoom: builder.mutation<
      Room,
      {
        /**
         * To be used solely in updateQueryData  for getRooms as it uses userId param.
         */
        userId: string;
        /**
         * To be used solely in updateQueryData  for getRooms as it uses userId param.
         */
        institutionId: number | null;
        roomId: string;
        payload: Partial<Room>;
      }
    >({
      query: (data) => {
        return {
          url: `rooms/${data.roomId}`,
          method: 'PUT',
          body: data.payload,
        };
      },
      async onQueryStarted(
        { userId, institutionId },
        { dispatch, queryFulfilled },
      ) {
        const { data: updatedRoom } = await queryFulfilled;
        dispatch(
          usersOGService.util.updateQueryData(
            'getRooms',
            {
              userId,
              institutionId,
            },
            (draft) => {
              draft[0] = updatedRoom;
            },
          ),
        );
      },
    }),
    getRooms: builder.query<
      Room[],
      {
        userId: string;
        institutionId: number | null;
      }
    >({
      query: (data) => ({
        url: `users/${data.userId}/rooms`,
        method: 'GET',
        params: {
          filter: JSON.stringify({
            where: {
              institutionId: data.institutionId,
            },
          }),
        },
      }),
      transformResponse: transformRooms,
      providesTags: ['rooms'],
    }),

    getClockDrift: builder.query<number, void>({
      query: () => ({
        url: `users/getCurrentTime`,
        method: 'GET',
      }),
      transformResponse: ({ ts }: LbCurrentTime) => Date.now() - ts,
    }),
    getLocationDetails: builder.mutation<LbLocationDetails, unknown>({
      query: () => {
        return {
          url: `users/getLocationDetails`,
          method: 'GET',
        };
      },
    }),
    updateWaitingRoomSettings: builder.mutation<
      Room,
      {
        payload: WaitingRoomSettingsFields;
      }
    >({
      query: (data) => ({
        url: `rooms/${data.payload.id}`,
        method: 'PUT',
        body: data.payload,
      }),
      invalidatesTags: ['rooms'],
    }),
    createWaitingRoom: builder.mutation<
      Room,
      { userId: string; payload: CreateWaitingRoomPayload }
    >({
      query: (data) => ({
        url: `users/${data.userId}/rooms`,
        method: 'POST',
        body: data.payload,
      }),
      invalidatesTags: ['rooms'],
    }),
    getAnalytics: builder.query<GetAnalyticsData, GetAnalyticsPayload>({
      query: (body) => {
        return {
          url: 'users/getAnalytics',
          method: 'POST',
          body,
        };
      },
      providesTags: ['analytics'],
    }),
    getAnalyticsOverview: builder.query<
      GetAnalyticsOverviewData,
      GetAnalyticsOverviewPayload
    >({
      query: (body) => {
        return {
          url: 'users/getAnalyticsOverview',
          method: 'POST',
          body,
        };
      },
      providesTags: ['analytics-overview'],
    }),

    getCustomerPortalLink: builder.query<string, void>({
      query: () => ({
        url: `stripe/customer-portal`,
        method: 'GET',
        params: {
          apollo: true,
        },
      }),
      transformResponse: (data: { url: string }) => data.url,
    }),
    getUserActivePurchase: builder.query<
      Purchase | undefined,
      { userId: string }
    >({
      query: (data) => ({
        url: `users/${data.userId}/purchases`,
        method: 'GET',
      }),
      transformResponse: (data: Purchase[]) =>
        data.find((purchase) => purchase.isActive),
    }),
    isValidPassword: builder.query<
      boolean,
      { userId: string; payload: { password: string } }
    >({
      query: (data) => ({
        url: `users/${data.userId}/isValidPassword`,
        method: 'POST',
        body: data.payload,
      }),
      transformResponse: (data: { isValid: boolean }) => data.isValid,
    }),
    isEmailUnique: builder.query<boolean, { email: string }>({
      query: (data) => ({
        url: `users/unique`,
        method: 'GET',
        params: {
          email: data.email,
        },
      }),
      transformResponse: (data: { unique: boolean }) => data.unique,
    }),
    deleteUser: builder.mutation<
      boolean,
      { userId: string; payload: { password: string } }
    >({
      query: (data) => ({
        url: `users/${data.userId}/deleteUser`,
        method: 'POST',
        body: {
          password: data.payload.password,
          user: {},
        },
      }),
      transformResponse: (data: { status: string }) =>
        data.status === 'USER_DELETED',
    }),
    getStatesLicensed: builder.query<LbStateLicensed[], { userId: string }>({
      query: (data) => ({
        url: `users/${data.userId}/locationsLicenced`,
        method: 'GET',
      }),
    }),
    updateStatesLicensed: builder.mutation<
      { locations: LbStateLicensed[] },
      {
        userId: string;
        payload: { country: string; state: string }[];
      }
    >({
      query: (data) => {
        return {
          url: `users/${data.userId}/locationsLicenced`,
          method: 'POST',
          body: data.payload,
        };
      },
      async onQueryStarted({ userId }, { dispatch, queryFulfilled }) {
        const { data: updatedStateLicensed } = await queryFulfilled;
        dispatch(
          usersOGService.util.updateQueryData(
            'getStatesLicensed',
            { userId },
            () => updatedStateLicensed.locations,
          ),
        );
      },
    }),
    getLinkedin: builder.mutation<
      LinkedInData,
      { userId: number | string; code: string; redirectUri: string }
    >({
      query: (data) => ({
        url: `users/${data.userId}/getLinkedin`,
        method: 'POST',
        body: {
          code: data.code,
          redirectUri: data.redirectUri,
        },
      }),
    }),

    rememberAuthState: builder.query<{}, { userId: string }>({
      // set 'transientAccessToken' cookie
      query: (data) => {
        return {
          url: `users/${data.userId}/rememberAuthState`,
          method: 'GET',
          params: {
            scope: 'provider-auth',
          },
          // allow set cookie in the browser from  Set-cookie response header
          credentials: 'include',
        };
      },
    }),
    deleteUserIdentity: builder.mutation<
      object,
      { userId: string; identityId: string }
    >({
      query: (data) => {
        return {
          url: `userIdentities/${data.identityId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['users'],
    }),
    validateCaptcha: builder.mutation<
      ValidateCaptchaResponseData,
      ValidateCaptchaRequestBody
    >({
      query: (body) => ({
        url: 'users/validateCaptcha',
        method: 'POST',
        body,
      }),
    }),
    getCustomer: builder.query<Customer, { userId: number | string }>({
      query: (data) => ({
        url: `users/${data.userId}/customer`,
        method: 'GET',
      }),
    }),
    addStripeSource: builder.mutation<
      Source,
      {
        userId: number | string;
        payload: { captchaJWTToken: string; token: string };
      }
    >({
      query: (data) => ({
        url: `users/${data.userId}/sources`,
        method: 'POST',
        body: data.payload,
      }),
    }),
    createDayPassPaymentIntent: builder.mutation<
      PaymentIntent,
      {
        captchaJWTToken: string;
        planId: string;
        coupon?: string;
      }
    >({
      query: (data) => ({
        url: 'users/createDayPassPaymentIntent',
        method: 'POST',
        body: data,
      }),
    }),
    completeDayPassPurchase: builder.mutation<
      { dayPassEndDate: Date },
      {
        paymentIntentId: string;
      }
    >({
      query: (data) => ({
        url: 'users/completeDaypassPurchase',
        method: 'POST',
        body: data,
      }),
    }),
    getWorkspaces: builder.query<LbWorkspaceResponse, { userId: string }>({
      query: () => ({
        url: `users/workspace`,
        method: 'GET',
      }),
    }),
    getCannyToken: builder.query<
      { token: string; userHash: string },
      { userId: string }
    >({
      query: (data) => {
        /**
         * data.userId pass as Frontegg users
         * StorageKeys.CurrentUserId using for non Frontegg users
         */
        const userId =
          data.userId || storage.findItem(StorageKeys.CurrentUserId);

        return {
          url: `/users/${userId}/getCannyToken`,
          method: 'GET',
        };
      },
    }),
    getAccessTokenFromJWT: builder.query<string, void>({
      query: () => {
        return {
          url: `/users/accessTokenFromJWT`,
          method: 'GET',
        };
      },
    }),
    updateFreeRegionCurrencyFields: builder.mutation<boolean, void>({
      query: () => ({
        url: 'users/updateFreeRegionCurrencyFieldsFromFrontegg',
        method: 'POST',
      }),
      invalidatesTags: ['users'],
    }),
  }),
});

export const {
  endpoints: {
    getUser,
    getRooms,
    getUserActivePurchase,
    getClockDrift,
    rememberAuthState,
    getCannyToken,
    getAccessTokenFromJWT,
    getWorkspaces,
  },
  useGetRoomsQuery,
  useUpdateRoomMutation,
  useGetUserQuery,
  useGetClockDriftQuery,
  useGetLocationDetailsMutation,
  useUpdateUserMutation,
  useSetPrototypeAppsOptInMutation,
  useUpdateWaitingRoomSettingsMutation,
  useCreateWaitingRoomMutation,
  useGetAnalyticsOverviewQuery,
  useGetAnalyticsQuery,
  useLazyGetCustomerPortalLinkQuery,
  useGetUserActivePurchaseQuery,
  useLazyIsValidPasswordQuery,
  useLazyIsEmailUniqueQuery,
  useLazyGetUserQuery,
  useLazyRememberAuthStateQuery,
  useDeleteUserIdentityMutation,
  useGetStatesLicensedQuery,
  useUpdateStatesLicensedMutation,
  useGetLinkedinMutation,
  useDeleteUserMutation,
  useValidateCaptchaMutation,
  useGetCustomerQuery,
  useAddStripeSourceMutation,
  useCreateDayPassPaymentIntentMutation,
  useCompleteDayPassPurchaseMutation,
  useGetWorkspacesQuery,
  useGetAccessTokenFromJWTQuery,
  useUpdateFreeRegionCurrencyFieldsMutation,
} = usersOGService;
